import { Heading } from '@boltenergy-be/design-system'
import DashboardCard from '../components/dashboard-card/DashboardCard.tsx'
import SalesDashboardChart from '../components/sales-dashboard-chart/SalesDashboardChart.tsx'
import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useGetProspectReportsQuery, useLazyGetProspectReportExportQuery } from 'store/queries/bolt-backend/reports'
import { isReportEmpty, mapMonthBreakdownToSalesDashboardChartEntry } from './utils.ts'
import { ExportProspectReportQueryStringParameters } from '@boltenergy-be/shared'
import { SALES_CHART_DEFAULT_PROPS } from './constants.ts'
import { popErrorToast } from '@boltenergy-be/design-system'
import { downloadFile } from 'utils/files.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'

const ProspectReports = () => {
  // Constants
  const today = useMemo(() => dayjs(), [])
  const from = today.subtract(7, 'months').startOf('month')

  // Redux queries
  const { currentData, isFetching } = useGetProspectReportsQuery({
    from: from.format('YYYY-MM'),
    to: today.format('YYYY-MM')
  })
  const [triggerExport] = useLazyGetProspectReportExportQuery()

  // Datasets
  const toBeConfirmedProspectsData = useMemo(
    () => mapMonthBreakdownToSalesDashboardChartEntry(currentData?.toBeConfirmed),
    [currentData?.toBeConfirmed]
  )
  const brutoSalesProspectsData = useMemo(
    () => mapMonthBreakdownToSalesDashboardChartEntry(currentData?.brutoSales),
    [currentData?.brutoSales]
  )

  /**
   * Export report to CSV through endpoint
   * @param {ExportProspectReportQueryStringParameters["report"]} report
   * @returns {Promise<void>}
   */
  const handleExport = async (report: ExportProspectReportQueryStringParameters['report']) => {
    try {
      const csv = await triggerExport({ report, from: from.format('YYYY-MM'), to: today.format('YYYY-MM') }).unwrap()
      downloadFile(csv, `${report}-${today.format('YYYY-MM-DD')}.csv`, 'text/csv;charset=utf-8;')
    } catch (err) {
      popErrorToast('Something went wrong while exporting the report')
      log({ error: err as Error, identifier: `[${Flow.REPORTS}:handleExport]` })
    }
  }

  return (
    <>
      <Heading as="h2" variant="h4">
        Opportunities / contracts not yet active
      </Heading>

      <DashboardCard
        variant="large"
        title="To be confirmed"
        description="Email is send, but not confirmed"
        loading={isFetching}
        handleExport={async () => await handleExport('toBeConfirmed')}
        disableExport={isReportEmpty(currentData?.toBeConfirmed)}
      >
        <SalesDashboardChart label="To be confirmed" {...SALES_CHART_DEFAULT_PROPS} data={toBeConfirmedProspectsData} />
      </DashboardCard>

      <DashboardCard
        variant="large"
        title="Bruto sales"
        description="No EAN, Closed won, No EAN cancel"
        loading={isFetching}
        handleExport={async () => await handleExport('brutoSales')}
        disableExport={isReportEmpty(currentData?.brutoSales)}
      >
        <SalesDashboardChart label="Bruto sales" {...SALES_CHART_DEFAULT_PROPS} data={brutoSalesProspectsData} />
      </DashboardCard>
    </>
  )
}

export default ProspectReports
